import { useState } from "react";

import { useIsMobile } from "../hooks/useIsMobile";
import { LineField } from "./LineField";
import { passwordProblem } from "../utils/passwordPolicy";
import { C, font } from "../styles/tokens";

/**
 * Reusable add/edit form for an organisation branch.
 *
 * A branch is always a location under a parent organisation -- this form
 * deliberately collects NO registration/GSTIN field, because the GSTIN lives
 * once on the parent organisation and is shared by every branch.
 *
 * Props:
 *   initial   -- existing branch when editing, omitted when adding
 *   onSubmit  -- async (values) => void; throws to surface a server error
 *   onCancel  -- optional; renders a Cancel button when supplied
 *   submitLabel
 */

const REQUIRED_MESSAGES = {
  name: "Branch name is required.",
  addressLine1: "Address is required.",
  city: "City is required.",
  state: "State is required.",
};

/**
 * Validates the branch form. Exported so tests (and callers) can check the
 * rules without mounting React -- the same function the component uses, so the
 * two can never drift.
 *
 * Returns a map of field -> message. Empty object means valid.
 */
export function validateBranch(
  values,
  { existingNames = [], existingCodes = [], isEditing = false } = {},
) {
  const errors = {};

  for (const [field, message] of Object.entries(REQUIRED_MESSAGES)) {
    if (!String(values[field] ?? "").trim()) errors[field] = message;
  }

  const email = String(values.email ?? "").trim();
  if (!email) {
    errors.email = "Email is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = "Enter a valid email address.";
  }

  const password = String(values.password ?? "");
  if (!isEditing && !password) {
    errors.password = "Password is required.";
  } else if (password && passwordProblem(password)) {
    // The rule for a person's password (decided 2 Oct 2026).
    errors.password = passwordProblem(password);
  }

  const pincode = String(values.pincode ?? "").trim();
  if (!pincode) {
    errors.pincode = "PIN code is required.";
  } else if (!/^[1-9][0-9]{5}$/.test(pincode)) {
    // Indian PIN codes are 6 digits and never start with 0.
    errors.pincode = "Enter a valid 6-digit Indian PIN code.";
  }

  // Uniqueness is scoped to THIS organisation -- the same branch name under a
  // different organisation is perfectly legal, so we only compare against the
  // names already present in this organisation's list.
  const name = String(values.name ?? "")
    .trim()
    .toLowerCase();
  if (name && existingNames.some((n) => n.toLowerCase() === name)) {
    errors.name = "This organisation already has a branch with that name.";
  }

  const code = String(values.branchCode ?? "").trim();
  if (code) {
    if (!/^[A-Za-z0-9._-]+$/.test(code)) {
      errors.branchCode =
        "Branch code may only contain letters, numbers, dots, hyphens and underscores.";
    } else if (
      existingCodes.some((c) => String(c).toLowerCase() === code.toLowerCase())
    ) {
      errors.branchCode =
        "This organisation already has a branch with that branch code.";
    }
  }

  return errors;
}

/**
 * The body the form sends. A blank optional field is left out, except the
 * branch code of an existing branch: sent empty, it clears the code. The
 * email is always sent; a changed one moves the branch login to it.
 */
export function branchSubmitValues(values, { isEditing = false } = {}) {
  const branchCode = values.branchCode.trim();
  return {
    name: values.name.trim(),
    addressLine1: values.addressLine1.trim(),
    city: values.city.trim(),
    state: values.state.trim(),
    pincode: values.pincode.trim(),
    branchCode: isEditing ? branchCode : branchCode || undefined,
    email: values.email.trim() || undefined,
    password: values.password ? values.password : undefined,
    isHeadOffice: !!values.isHeadOffice,
  };
}

const EMPTY = {
  name: "",
  addressLine1: "",
  city: "",
  state: "",
  pincode: "",
  branchCode: "",
  email: "",
  password: "",
  isHeadOffice: false,
};

export function BranchForm({
  initial,
  existingNames = [],
  existingCodes = [],
  onSubmit,
  onCancel,
  submitLabel = "Save branch",
}) {
  const isMobile = useIsMobile();
  const [values, setValues] = useState({ ...EMPTY, ...(initial ?? {}) });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  function update(field, value) {
    setValues((v) => ({ ...v, [field]: value }));
    // Clear the field's error as soon as the user starts fixing it.
    setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setServerError(null);

    const found = validateBranch(values, {
      existingNames,
      existingCodes,
      isEditing: Boolean(initial),
    });
    if (Object.keys(found).length > 0) {
      // Stop here -- no request is made with invalid data.
      setErrors(found);
      return;
    }

    setBusy(true);
    try {
      await onSubmit(
        branchSubmitValues(values, { isEditing: Boolean(initial) }),
      );
      if (!initial) setValues({ ...EMPTY });
      setErrors({});
    } catch (err) {
      setServerError(
        err?.message ?? "Could not save the branch. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate style={{ marginTop: 4 }}>
      <div
        className={
          isMobile
            ? "flex flex-col gap-3"
            : "flex flex-col sm:flex-row gap-3 items-stretch sm:items-start"
        }
      >
        <div
          className={
            isMobile ? "w-full" : "w-full sm:flex-[2] sm:min-w-[190px]"
          }
        >
          <LineField
            label="Branch name *"
            placeholder="e.g. Chennai Branch"
            value={values.name}
            onChange={(e) => update("name", e.target.value)}
          />
          <FieldError message={errors.name} />
        </div>
        <div
          className={isMobile ? "w-full" : "w-full sm:flex-1 sm:min-w-[130px]"}
        >
          <LineField
            label="Branch code"
            placeholder="e.g. CHN001"
            value={values.branchCode}
            onChange={(e) => update("branchCode", e.target.value)}
            hint="Optional. Unique within this organisation."
          />
          <FieldError message={errors.branchCode} />
        </div>
      </div>

      <div style={{ marginTop: isMobile ? 3 : 0 }}>
        <LineField
          label="Address *"
          placeholder="e.g. 12 Anna Salai, Guindy"
          value={values.addressLine1}
          onChange={(e) => update("addressLine1", e.target.value)}
        />
        <FieldError message={errors.addressLine1} />
      </div>

      <div
        className={
          isMobile
            ? "flex flex-col gap-3"
            : "flex flex-col sm:flex-row gap-3 items-stretch sm:items-start"
        }
      >
        <div
          className={isMobile ? "w-full" : "w-full sm:flex-1 sm:min-w-[130px]"}
        >
          <LineField
            label="City *"
            placeholder="e.g. Chennai"
            value={values.city}
            onChange={(e) => update("city", e.target.value)}
          />
          <FieldError message={errors.city} />
        </div>
        <div
          className={isMobile ? "w-full" : "w-full sm:flex-1 sm:min-w-[130px]"}
        >
          <LineField
            label="State *"
            placeholder="e.g. Tamil Nadu"
            value={values.state}
            onChange={(e) => update("state", e.target.value)}
          />
          <FieldError message={errors.state} />
        </div>
        <div
          className={isMobile ? "w-full" : "w-full sm:flex-1 sm:min-w-[110px]"}
        >
          <LineField
            label="PIN code *"
            placeholder="e.g. 600001"
            inputMode="numeric"
            value={values.pincode}
            onChange={(e) =>
              update("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))
            }
          />
          <FieldError message={errors.pincode} />
        </div>
      </div>

      <div
        className={
          isMobile
            ? "flex flex-col gap-3"
            : "flex flex-col sm:flex-row gap-3 items-stretch sm:items-start"
        }
      >
        <div
          className={isMobile ? "w-full" : "w-full sm:flex-1 sm:min-w-[160px]"}
        >
          <LineField
            label="Email *"
            type="email"
            placeholder="e.g. branch.chennai@infosys.com"
            value={values.email}
            onChange={(e) => update("email", e.target.value)}
            autoComplete="email"
            hint={
              initial
                ? "Changing it moves the branch login to the new email; the old email stops working."
                : undefined
            }
          />
          <FieldError message={errors.email} />
        </div>
        <div
          className={
            isMobile
              ? "w-full relative"
              : "w-full sm:flex-1 sm:min-w-[160px] relative"
          }
        >
          <LineField
            label={initial ? "Password" : "Password *"}
            type={showPassword ? "text" : "password"}
            value={values.password}
            onChange={(e) => update("password", e.target.value)}
            autoComplete="new-password"
            hint={
              initial
                ? "Leave blank to keep the current password. A new one signs the branch out everywhere."
                : undefined
            }
          />
          <button
            type="button"
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: "absolute",
              right: 2,
              top: isMobile ? 18 : 22,
              background: "transparent",
              border: 0,
              cursor: "pointer",
              color: C.textMed,
              fontSize: 12,
              fontWeight: 600,
              padding: isMobile ? "8px 10px" : "4px 6px",
              minHeight: isMobile ? 44 : "auto",
              display: "flex",
              alignItems: "center",
              fontFamily: font,
            }}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
          <FieldError message={errors.password} />
        </div>
      </div>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          color: C.textMed,
          margin: isMobile ? "8px 0 16px" : "6px 0 12px",
          minHeight: isMobile ? 44 : "auto",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={!!values.isHeadOffice}
          onChange={(e) => update("isHeadOffice", e.target.checked)}
          style={isMobile ? { width: 18, height: 18 } : undefined}
        />
        This is the head office
      </label>

      {serverError && (
        <div style={{ fontSize: 12, color: C.red, marginBottom: 8 }}>
          {serverError}
        </div>
      )}

      <div
        className={
          isMobile
            ? "flex flex-col gap-2 pt-1"
            : "flex flex-col sm:flex-row gap-2 pt-1"
        }
      >
        <button
          type="submit"
          disabled={busy}
          className="min-h-[44px] sm:min-h-0 w-full sm:w-auto px-4 py-2 text-sm sm:text-[13px] font-semibold text-white rounded transition flex items-center justify-center cursor-pointer disabled:cursor-not-allowed"
          style={primaryButtonStyle(busy)}
        >
          {busy ? "Saving..." : submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="min-h-[44px] sm:min-h-0 w-full sm:w-auto px-4 py-2 text-sm sm:text-[13px] font-semibold rounded transition flex items-center justify-center cursor-pointer"
            style={secondaryButtonStyle}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return (
    <div role="alert" style={{ fontSize: 11, color: C.red, marginTop: 2 }}>
      {message}
    </div>
  );
}

const rowStyle = {
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
  alignItems: "flex-start",
};

const primaryButtonStyle = (busy) => ({
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 600,
  color: "white",
  background: busy ? C.textLight : C.blue,
  border: 0,
  borderRadius: 4,
  cursor: busy ? "default" : "pointer",
  fontFamily: font,
});

const secondaryButtonStyle = {
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 600,
  color: C.textMed,
  background: "transparent",
  border: `1px solid ${C.dividerStrong}`,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: font,
};
